import React, {useRef, useState, useCallback, useEffect} from 'react';

import {AttributePermissionIndexed} from '../../types';
import type {VirtualizedListItemProps} from '@netx/core-components/VirtualizedList';
import VirtualizedPermissionListItem, {
	type VirtualizedPermissionListItemDataProps,
} from '../VirtualizedListItem';

import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import useUpdateEffect from '@netx/core-components/hooks/useUpdateEffect';

import PermissionItemFocusTrapMenu, {
	type PermissionItemFocusTrapMenuProps,
} from './PermissionItemMenuFocusTrap';

import {useDocumentKeyDown} from '@netx/core-components/hooks/useDocumentKeyDown';

const preventBubble = (event: React.FocusEvent) => event.stopPropagation();
export interface VirtualizedPermissionListItemFocusTrapDataProps
	extends VirtualizedPermissionListItemDataProps {
	activeIndex: number;
	focusTrapActive?: boolean;
	onInteracting?: (index: number, interacting: boolean) => void;
}
export interface VirtualizedPermissionListItemFocusTrapProps
	extends VirtualizedListItemProps<
		AttributePermissionIndexed,
		VirtualizedPermissionListItemFocusTrapDataProps
	> {}

const VirtualizedPermissionListItemFocusTrap = ({
	data,
	index,
	...props
}: VirtualizedPermissionListItemFocusTrapProps) => {
	const itemMenuRef = useRef<any>(null);
	const justTabbedForwardRef = useRef(false);
	const [menuOpen, setMenuOpen] = useState(false);

	const {rows, onClick} = data;
	const {activeIndex, focusTrapActive, onInteracting, ...sendProps} = data;
	const permission = rows[index];

	const [menuActive, setMenuActive] = useState(false);

	// When the `groupId` changes, reset the menu states
	useUpdateEffect(() => {
		setMenuOpen(false);
		setMenuActive(false);
	}, [permission?.groupId]);

	// When the menu closes, remove the activeness so the user can get to the next
	// item without having to shift+tab
	useUpdateEffect(() => {
		if (!menuOpen) setMenuActive(false);
	}, [menuOpen]);

	// Let the outside world know when this item is being interacted with
	useWhenChangedEffect(
		[menuOpen, menuActive] as const,
		useCallback(
			([menuOpen, menuActive]) => {
				onInteracting?.(index, menuOpen || menuActive);
			},
			[onInteracting, index],
		),
	);

	// Reset some things when `activeIndex` no longer matches this `index`
	useWhenChangedEffect(
		[activeIndex, index] as const,
		useCallback(
			([activeIndex, index]) => {
				if (activeIndex === index) return;
				setMenuActive(false);
				menuOpen && itemMenuRef.current?.close();
			},
			[menuOpen],
		),
	);

	// TODO: move this to a shared hook or whatever if we continue down this path
	// this handles focus within the item and triggering a menu
	useDocumentKeyDown(
		useCallback(
			(event) => {
				switch (event.key) {
					case ' ':
						// The damn space key!
						setTimeout(() => setMenuOpen(true), 101);
						return false;
					case 'Enter':
						setMenuOpen(true);
						return false;
					case 'ArrowDown':
					case 'ArrowUp':
						if (menuActive) {
							setMenuOpen(true);
							return false;
						}
						return;
					case 'Tab':
						justTabbedForwardRef.current = false;
						if (menuActive && event.shiftKey) {
							setMenuActive(false);
							return false;
						}
						if (!menuActive && !event.shiftKey) {
							setMenuActive(true);
							return false;
						}
						if (menuActive) {
							justTabbedForwardRef.current = true;
						}
						return;
				}
			},
			[menuActive],
		),
		{
			disabled: !focusTrapActive || activeIndex !== index || menuOpen,
		},
	);

	// When the menu opens or closes, need to get that focus back to the item at this index
	const handleMenuOpenedToggle = useCallback(
		(open: boolean) => (requested: boolean) => {
			setMenuOpen((wasOpen) => {
				// If it was not requested and it changed, ensure focus is brought back to the item at this index
				if (wasOpen !== open) !requested && onClick?.(index);
				return open;
			});
		},
		[onClick],
	);

	// When the menu fires off a blur
	const handleMenuBlur = useCallback(
		(event: React.FocusEvent) => {
			// The user is trying to escape the "focus trap"
			if (justTabbedForwardRef.current) return;
			justTabbedForwardRef.current = false;
			// Do not let this bubble up
			preventBubble(event);
			// Ensure focus is brought back to the item at this index
			onClick?.(index);
		},
		[onClick],
	);

	const menuToggleId = `permission-menu-toggle-${index.toString()}`;
	const selected = (focusTrapActive || menuOpen) && index === activeIndex;

	return (
		<VirtualizedPermissionListItem<PermissionItemFocusTrapMenuProps>
			{...props}
			index={index}
			data={
				{
					...sendProps,
					selected,
					'aria-selected': selected,
					'aria-owns': menuToggleId,
					onFocusCapture: preventBubble,
					tabIndex: -1,
					PermissionItemMenuComponent: PermissionItemFocusTrapMenu,
					PermissionItemMenuProps: {
						...data.PermissionItemMenuProps,
						canTab: activeIndex === index && focusTrapActive,
						focused: menuActive,
						onBlur: handleMenuBlur,
						onClose: handleMenuOpenedToggle(false),
						onOpen: handleMenuOpenedToggle(true),
						open: menuOpen,
						ButtonProps: {
							...data.PermissionItemMenuProps?.ButtonProps,
							id: menuToggleId,
						},
					},
				} as any
			}
		/>
	);
};

export default VirtualizedPermissionListItemFocusTrap;
